import { useState, useEffect } from 'react'

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

// Capture the event globally so it's not missed before React mounts
let deferredPromptGlobal: BeforeInstallPromptEvent | null = null
const listeners = new Set<(e: BeforeInstallPromptEvent | null) => void>()

window.addEventListener('beforeinstallprompt', (e) => {
  e.preventDefault()
  deferredPromptGlobal = e as BeforeInstallPromptEvent
  listeners.forEach((fn) => fn(deferredPromptGlobal))
})

export function usePWAInstall() {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(deferredPromptGlobal)

  useEffect(() => {
    listeners.add(setDeferredPrompt)
    // Pick up if it was already captured
    if (deferredPromptGlobal) setDeferredPrompt(deferredPromptGlobal)
    return () => { listeners.delete(setDeferredPrompt) }
  }, [])

  const install = async () => {
    if (!deferredPrompt) return
    deferredPrompt.prompt()
    const { outcome } = await deferredPrompt.userChoice
    if (outcome === 'accepted') {
      deferredPromptGlobal = null
      setDeferredPrompt(null)
    }
  }

  return { canInstall: !!deferredPrompt, install }
}
